import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { getStaff } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const ROLE_LABELS = { admin: 'Admin', readonly: 'Read Only', attachments: 'Attachments Only', staff: 'Staff', contractor: 'Contractor' };
const ROLE_COLORS = { admin: '#1a355f', readonly: '#1565C0', attachments: '#E65100', staff: '#6a1a9a', contractor: '#0277bd' };
const ROLE_DESC = {
  admin: 'Full access — can view, add, edit, and delete everything.',
  readonly: 'Can view all pages and data but cannot add, edit, or delete anything.',
  attachments: 'Can only upload attachments to existing jobs. No other access.',
  staff: 'Can only see their own jobs and job status. Linked to their name in the staff list.',
  contractor: 'Can see assigned jobs, update status, upload photos, and request change orders.',
};

export default function Users() {
  const { startImpersonation } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [contractorList, setContractorList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ username: '', password: '', role: 'readonly', staff_name: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadUsers(); loadStaff(); loadContractors(); }, []);

  async function loadUsers() {
    try {
      const res = await api.get('/auth/users');
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }

  async function loadStaff() {
    try {
      const data = await getStaff();
      setStaffList(Array.isArray(data) ? data.filter(s => s.status === 'Active') : []);
    } catch {}
  }

  async function loadContractors() {
    try {
      const res = await api.get('/contractors');
      const data = Array.isArray(res.data) ? res.data : [];
      setContractorList(data.filter(c => c.status === 'Active'));
    } catch {}
  }

  function openNew() {
    setEditUser(null);
    setForm({ username: '', password: '', role: 'readonly', staff_name: '' });
    setShowForm(true);
  }

  function openEdit(user) {
    setEditUser(user);
    setForm({ username: user.username, password: '', role: user.role || 'admin', staff_name: user.staff_name || '' });
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.username.trim()) return toast.error('Username required');
    if (!editUser && !form.password.trim()) return toast.error('Password required for new user');
    if (form.role === 'staff' && !form.staff_name) return toast.error('Please select a staff member');
    if (form.role === 'contractor' && !form.staff_name) return toast.error('Please select a contractor company');
    setSaving(true);
    try {
      const payload = { username: form.username, role: form.role, staff_name: (form.role === 'staff' || form.role === 'contractor') ? form.staff_name : null };
      if (form.password.trim()) payload.password = form.password;
      if (editUser) {
        await api.put(`/auth/users/${editUser.id}`, payload);
        toast.success('User updated');
      } else {
        await api.post('/auth/users', { ...payload, password: form.password });
        toast.success('User created');
      }
      setShowForm(false);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  }

  async function handleDelete(user) {
    if (!window.confirm(`Delete user "${user.username}"?`)) return;
    try {
      await api.delete(`/auth/users/${user.id}`);
      toast.success('User deleted');
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to delete'); }
  }

  async function handleImpersonate(user) {
    if (!window.confirm(`Log in as "${user.username}" to see exactly what they see? You can return to admin anytime via the banner.`)) return;
    try {
      const res = await api.post(`/impersonate/${user.id}`);
      startImpersonation(res.data.token, res.data.username, res.data.role, res.data.staff_name);
      toast.success(`Now viewing as ${user.username}`);
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not log in as this user');
    }
  }

  return (
    <>
      <div className="page-header">
        <h2>User Management</h2>
        <div className="page-header-actions">
          <button className="btn btn-primary btn-sm" onClick={openNew}>+ Add User</button>
        </div>
      </div>

      <div className="page-body">
        <div className="card">
          <div className="card-header"><h3>All Users</h3></div>
          <div style={{ padding: '0 0 8px' }}>
            {loading ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>Loading…</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: '#0d1f3b', color: '#fff' }}>
                    <th style={{ padding: '10px 16px', textAlign: 'left' }}>Username</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left' }}>Role</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left' }}>Linked Name</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left' }}>Created</th>
                    <th style={{ padding: '10px 16px', textAlign: 'left' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, idx) => (
                    <tr key={user.id} style={{ background: idx % 2 === 0 ? '#f9f9f9' : '#fff', borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '10px 16px', fontWeight: 600 }}>{user.username}</td>
                      <td style={{ padding: '10px 16px' }}>
                        <span style={{ background: ROLE_COLORS[user.role] || '#555', color: '#fff', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                          {ROLE_LABELS[user.role] || user.role}
                        </span>
                      </td>
                      <td style={{ padding: '10px 16px', color: '#555' }}>{user.staff_name || '—'}</td>
                      <td style={{ padding: '10px 16px', color: '#777', fontSize: 13 }}>{user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}</td>
                      <td style={{ padding: '10px 16px' }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="btn btn-outline btn-sm" onClick={() => openEdit(user)}>Edit</button>
                          <button className="btn btn-ghost btn-sm" style={{ color: '#6a1a9a' }} onClick={() => handleImpersonate(user)}>👁️ Log in as</button>
                          <button className="btn btn-ghost btn-sm" style={{ color: '#c62828' }} onClick={() => handleDelete(user)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Role descriptions */}
        <div style={{ display: 'flex', gap: 16, marginTop: 20, flexWrap: 'wrap' }}>
          {Object.entries(ROLE_LABELS).map(([key, label]) => (
            <div key={key} style={{ background: '#fff', border: `2px solid ${ROLE_COLORS[key]}`, borderRadius: 8, padding: '12px 16px', minWidth: 200, flex: 1 }}>
              <div style={{ fontWeight: 700, color: ROLE_COLORS[key], marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 13, color: '#555' }}>{ROLE_DESC[key]}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 28, width: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <h3 style={{ margin: '0 0 20px', color: '#0d1f3b' }}>{editUser ? 'Edit User' : 'Add New User'}</h3>

            <div className="form-group" style={{ marginBottom: 14 }}>
              <label className="form-label">Username</label>
              <input className="form-input" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} />
            </div>

            <div className="form-group" style={{ marginBottom: 14 }}>
              <label className="form-label">{editUser ? 'New Password (leave blank to keep)' : 'Password'}</label>
              <input className="form-input" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
            </div>

            <div className="form-group" style={{ marginBottom: 14 }}>
              <label className="form-label">Role</label>
              <select className="form-select" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value, staff_name: '' }))}>
                <option value="admin">Admin — Full Access</option>
                <option value="readonly">Read Only — View Everything</option>
                <option value="attachments">Attachments Only — Upload Files</option>
                <option value="staff">Staff — See Own Jobs Only</option>
                <option value="contractor">Contractor — See Assigned Jobs</option>
              </select>
            </div>

            {form.role === 'staff' && (
              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="form-label">Linked Staff Name</label>
                <select className="form-select" value={form.staff_name} onChange={e => setForm(f => ({ ...f, staff_name: e.target.value }))}>
                  <option value="">— Select Staff Member —</option>
                  {staffList.map(s => <option key={s.id} value={s.name}>{s.name} ({s.role})</option>)}
                </select>
              </div>
            )}

            {form.role === 'contractor' && (
              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="form-label">Linked Contractor Company</label>
                <select className="form-select" value={form.staff_name} onChange={e => setForm(f => ({ ...f, staff_name: e.target.value }))}>
                  <option value="">— Select Contractor —</option>
                  {contractorList.map(c => <option key={c.id} value={c.company_name}>{c.company_name}</option>)}
                </select>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editUser ? 'Update User' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
